import { HourglassIcon, TypeJobRequestIcon, WebsiteIcon } from "@eworkit/iris";
import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { UserContext } from "@/components/User";
import { IEmailPreferences } from "@/data/email-preferences";
import { Flex } from "@/eworkitComponents";
import { CustomFiltersSettings } from "@/views/Settings/NotificationSettings/EmailNotifications/CustomFiltersSettings";

import { EmailNotificationSetting } from "./EmailNotificationSetting";
import { EmailNotificationSettingsGroup } from "./EmailNotificationSettingsGroup";

interface IRecommendedJobsSettingsProps {
  emailPreferences: IEmailPreferences;
  onChange?: <T extends IEmailPreferences, K extends keyof T>(key: K, value: T[K]) => void;
}

export const RecommendedJobsSettings: FC<IRecommendedJobsSettingsProps> = ({ emailPreferences, onChange }) => {
  const { t } = useTranslation("session");

  const { hasUserRoleInContextCompany, contextCompany } = useContext(UserContext);

  const isPrivateProfile = !contextCompany;

  const canToggleJobRequestExpiredSetting = hasUserRoleInContextCompany?.("ADMIN", "MANAGER");
  const canToggleJobPromotionsSettings = hasUserRoleInContextCompany?.("ADMIN", "MANAGER") || isPrivateProfile;
  const canTogglePrivatePromotionsSettings = hasUserRoleInContextCompany?.("ADMIN", "MANAGER") || isPrivateProfile;

  return (
    <Flex direction="column" innerMargin="lg">
      <EmailNotificationSettingsGroup
        header={t("USERS.SETTINGS.EMAIL_NOTIFICATIONS.RECOMMENDED_JOBS.HEADER")}
        frequencyTooltipText={t("USERS.SETTINGS.EMAIL_NOTIFICATIONS.JOBS_FILTERS.FREQUENCY_TOOLTIP")}>
        <EmailNotificationSetting
          icon={TypeJobRequestIcon}
          name={t("USERS.SETTINGS.EMAIL_NOTIFICATIONS.RECOMMENDED_JOBS.DAILY_JOB_UPDATES")}
          isChecked={emailPreferences.dailyJobUpdateEnabled}
          onChange={value => onChange?.("dailyJobUpdateEnabled", !!value)}
        />
        {canToggleJobRequestExpiredSetting && (
          <EmailNotificationSetting
            icon={HourglassIcon}
            name={t("USERS.SETTINGS.EMAIL_NOTIFICATIONS.RECOMMENDED_JOBS.JOB_REQUEST_EXPIRED")}
            isChecked={emailPreferences.jobRequestExpiredEnabled}
            onChange={value => onChange?.("jobRequestExpiredEnabled", !!value)}
          />
        )}
        {canToggleJobPromotionsSettings && (
          <EmailNotificationSetting
            icon={WebsiteIcon}
            name={t("USERS.SETTINGS.EMAIL_NOTIFICATIONS.RECOMMENDED_JOBS.JOB_PROMOTIONS")}
            isChecked={emailPreferences.jobPromotionsEnabled}
            onChange={value => onChange?.("jobPromotionsEnabled", !!value)}
          />
        )}
        {canTogglePrivatePromotionsSettings && (
          <EmailNotificationSetting
            icon={WebsiteIcon}
            name={t("USERS.SETTINGS.EMAIL_NOTIFICATIONS.RECOMMENDED_JOBS.PRIVATE_PROMOTIONS")}
            isChecked={emailPreferences.privatePromotionsEnabled}
            onChange={value => onChange?.("privatePromotionsEnabled", !!value)}
          />
        )}
        <CustomFiltersSettings />
      </EmailNotificationSettingsGroup>
    </Flex>
  );
};
